import { useState } from "react";
import { toast } from "sonner";
import { C, PageHeader, GlassCard, Badge, Button, Skeleton } from "@coremonit/design-system";
import { Beaker, Play, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useRunTest, useServices } from "../api/hooks";

const TESTS: Array<{ kind: string; label: string; description: string }> = [
  { kind: "connection",  label: "Connection test",   description: "Reachability, TLS, auth handshake." },
  { kind: "metrics",     label: "Metrics test",      description: "Sample scrape → validate schema + labels." },
  { kind: "trace",       label: "Trace test",        description: "Emit a synthetic trace and confirm ingest." },
  { kind: "log",         label: "Log test",          description: "Tail one line — assert delivered within 2s." },
  { kind: "deployment",  label: "Deployment test",   description: "Dry-run kubectl apply, sync check." },
  { kind: "container",   label: "Container test",    description: "Pull image, run readiness probe." },
  { kind: "synthetic",   label: "Synthetic request", description: "Full end-to-end request against /healthz." },
  { kind: "stress",      label: "Stress test",       description: "Ramp to 2× baseline for 60s." },
];

export function TestingPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const run = useRunTest();
  const [results, setResults] = useState<Record<string, Awaited<ReturnType<typeof run.mutateAsync>> | "running">>({});

  const runTest = async (kind: string) => {
    if (!serviceId) { toast.error("Pick a service first"); return; }
    setResults((r) => ({ ...r, [kind]: "running" }));
    try {
      const r = await run.mutateAsync({ serviceId, kind });
      setResults((prev) => ({ ...prev, [kind]: r }));
      toast[r.passed ? "success" : "error"](`${kind} ${r.passed ? "passed" : "failed"} in ${r.durationMs}ms`);
    } catch (e) {
      toast.error(`Test failed: ${(e as Error).message}`);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<Beaker size={16} />} title="Testing Center" subtitle="Real, on-demand probes against a service — connection, metrics, trace, log, deploy, container, synthetic, stress." />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select value={serviceId ?? ""} onChange={(e) => { setServiceId(e.target.value || undefined); setResults({}); }}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}>
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
        {TESTS.map((t) => {
          const r = results[t.kind];
          return (
            <GlassCard key={t.kind} style={{ padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{t.label}</div>
                  <div style={{ color: C.textMuted, fontSize: 10, marginTop: 3 }}>{t.description}</div>
                </div>
                {r === "running" ? <Loader2 size={16} color={C.blue} style={{ animation: "cm-spin 1s linear infinite" }} /> :
                 r?.passed          ? <CheckCircle2 size={16} color={C.green} /> :
                 r                   ? <XCircle size={16} color={C.red} /> : null}
              </div>
              {r && r !== "running" && (
                <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, marginTop: 8, fontSize: 10, color: C.textSub }}>
                  {r.checks.map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0" }}>
                      <span>{c.label}</span>
                      <Badge color={c.status === "passed" ? "green" : c.status === "warning" ? "amber" : "red"}>{c.durationMs}ms</Badge>
                    </div>
                  ))}
                  <div style={{ marginTop: 6, color: C.textMuted, fontSize: 9 }}>p95 {r.metrics.p95}ms · p99 {r.metrics.p99}ms · {r.metrics.throughputRps} rps</div>
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <Button variant="secondary" size="sm" icon={<Play size={12} />} onClick={() => runTest(t.kind)} disabled={r === "running"}>
                  {r === "running" ? "Running…" : r ? "Run again" : "Run test"}
                </Button>
              </div>
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
}
